import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase';
import { collection, doc, getDoc } from 'firebase/firestore'; 
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Helper function from Dashboard.js
const getLastSevenDays = () => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        // Format: YYYY-MM-DD
        dates.push(d.toISOString().split('T')[0]);
    }
    return dates;
};

// --- Report Content Component (What will be converted to PDF) ---
const ReportContent = ({ weeklyProgress, dietPlanData, userGoalsAndCurrentStats }) => {
    // Determine which days actually have data logged (meal adherence OR stats)
    // We will only render these days in the table.
    const loggedDays = weeklyProgress.filter(day => 
        (Array.isArray(day.adherence) && day.adherence.length > 0) || 
        day.stats.weight > 0 || 
        day.stats.sleepTime > 0 || 
        day.stats.waterIntake > 0 ||
        day.stats.exerciseTime > 0
    );

    // Summary Calculations now use ONLY loggedDays for adherence count
    const totalMeals = dietPlanData?.mealTimings?.length * loggedDays.length || 0;
    const completedMeals = loggedDays.reduce((sum, day) => {
        return sum + (Array.isArray(day.adherence) ? day.adherence.filter(a => a).length : 0);
    }, 0);
    const adherencePercentage = totalMeals > 0 ? ((completedMeals / totalMeals) * 100).toFixed(0) : '0';
    
    // Weight logic: Prioritize overall stats, fallback to logs
    const initialWeight = userGoalsAndCurrentStats?.initialWeight || 0;
    const currentWeight = userGoalsAndCurrentStats?.currentWeight || 0;
    
    // Find the most recent day that has a recorded weight (not 0) in the full weeklyProgress array
    const recentProgress = [...weeklyProgress].reverse(); 
    const currentDayStatsFromLogs = recentProgress.find(day => day.stats.weight > 0)?.stats;
    // Find the oldest day that has a recorded weight (not 0)
    const initialDayStatsFromLogs = weeklyProgress.find(day => day.stats.weight > 0)?.stats;
    
    const finalCurrentWeight = (currentWeight > 0 ? currentWeight : currentDayStatsFromLogs?.weight) || 0;
    const finalInitialWeight = (initialWeight > 0 ? initialWeight : initialDayStatsFromLogs?.weight) || 0;

    const weightLoss = (finalInitialWeight > 0 && finalCurrentWeight > 0) 
        ? (finalInitialWeight - finalCurrentWeight).toFixed(1) 
        : 'N/A';
    
    // Day names for the table header
    const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return (
        <div style={{ padding: '30px', backgroundColor: '#f9f9f9', color: '#333', minHeight: '800px', fontFamily: 'Arial, sans-serif' }}>
            
            {/* Header: Nutrition Trends */}
            <div style={{ paddingBottom: '15px', borderBottom: '3px solid #4CAF50', marginBottom: '30px' }}>
                <h1 style={{ color: '#4CAF50', margin: '0', fontSize: '2em' }}>Nutrition Trends</h1>
                <p style={{ color: '#555', margin: '5px 0 0 0', fontSize: '1.1em' }}>Weekly Progress Report</p>
                <p style={{ margin: '0', fontSize: '0.9em' }}>Report Date: {new Date().toLocaleDateString()}</p>
                <p style={{ margin: '0', fontSize: '0.9em' }}>Plan Name: {dietPlanData?.name || 'N/A'}</p>
            </div>

            {/* General Summary */}
            <h2 style={{ color: '#00796b', marginTop: '0px', borderBottom: '1px solid #ddd' }}>Summary</h2>
            <div style={{ display: 'flex', gap: '20px', marginBottom: '30px', justifyContent: 'space-between' }}>
                <div style={{ border: '1px solid #ccc', borderRadius: '8px', padding: '15px', flex: 1, backgroundColor: '#fff', boxShadow: '2px 2px 5px rgba(0,0,0,0.05)' }}>
                    <p style={{ margin: '0 0 5px 0', fontSize: '1.1em', color: '#555' }}>Meals Adherence (Logged Days):</p>
                    <strong style={{ fontSize: '2em', color: adherencePercentage >= 70 ? '#4CAF50' : '#ff9800' }}>{adherencePercentage}%</strong>
                </div>
                <div style={{ border: '1px solid #ccc', borderRadius: '8px', padding: '15px', flex: 1, backgroundColor: '#fff', boxShadow: '2px 2px 5px rgba(0,0,0,0.05)' }}>
                    <p style={{ margin: '0 0 5px 0', fontSize: '1.1em', color: '#555' }}>Total Weight Change:</p>
                    <strong style={{ fontSize: '2em', color: weightLoss >= 0 && weightLoss !== 'N/A' ? '#4CAF50' : (weightLoss === 'N/A' ? '#333' : '#f44336') }}>{weightLoss} kgs</strong>
                    {finalInitialWeight > 0 && <span style={{ fontSize: '0.8em', color: '#777' }}><br/>({finalCurrentWeight.toFixed(1)} kgs)</span>}
                </div>
            </div>
            
            {/* Goals Snapshot using fetched userStats */}
            <h2 style={{ color: '#00796b', marginTop: '30px', borderBottom: '1px solid #ddd' }}>Current Goals</h2>
            <div style={{ display: 'flex', gap: '20px', marginBottom: '30px', flexWrap: 'wrap' }}>
                <div style={{ border: '1px solid #eee', padding: '10px', flex: 1, minWidth: '150px' }}>
                    <strong>Initial Weight:</strong> {finalInitialWeight.toFixed(1)} kgs
                </div>
                <div style={{ border: '1px solid #eee', padding: '10px', flex: 1, minWidth: '150px' }}>
                    <strong>Weight Goal:</strong> {userGoalsAndCurrentStats?.weightGoal ? userGoalsAndCurrentStats.weightGoal.toFixed(1) + ' kgs' : 'N/A'}
                </div>
                <div style={{ border: '1px solid #eee', padding: '10px', flex: 1, minWidth: '150px' }}>
                    <strong>Sleep Goal:</strong> {userGoalsAndCurrentStats?.sleepGoal ? userGoalsAndCurrentStats.sleepGoal.toFixed(1) + ' hrs' : 'N/A'}
                </div>
                <div style={{ border: '1px solid #eee', padding: '10px', flex: 1, minWidth: '150px' }}>
                    <strong>Water Goal:</strong> {userGoalsAndCurrentStats?.waterGoal ? userGoalsAndCurrentStats.waterGoal.toFixed(1) + ' L' : 'N/A'}
                </div>
            </div>

            {/* Daily Breakdown Table */}
            <h2 style={{ color: '#00796b', marginTop: '30px', borderBottom: '1px solid #ddd' }}>Daily Progress Breakdown</h2>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px', fontSize: '0.9em' }}>
                <thead>
                    <tr style={{ backgroundColor: '#e8f5e9' }}>
                        <th style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'left' }}>Date (Day)</th>
                        <th style={{ border: '1px solid #ddd', padding: '10px' }}>Meals (%)</th>
                        <th style={{ border: '1px solid #ddd', padding: '10px' }}>Weight (kgs)</th>
                        <th style={{ border: '1px solid #ddd', padding: '10px' }}>Sleep (hrs)</th>
                        <th style={{ border: '1px solid #ddd', padding: '10px' }}>Water (L)</th>
                        <th style={{ border: '1px solid #ddd', padding: '10px' }}>Exercise (min)</th>
                    </tr>
                </thead>
                <tbody>
                    {/* ⭐ CORRECTION APPLIED HERE: Filtered list for table rendering */}
                    {/* Reverse the loggedDays array so the most recent day is on top */}
                    {[...loggedDays].reverse().map((day, index) => { 
                        const completed = Array.isArray(day.adherence) ? day.adherence.filter(a => a).length : 0;
                        const total = dietPlanData?.mealTimings?.length || 0;
                        const dailyPercent = total > 0 ? ((completed / total) * 100).toFixed(0) : 'N/A';
                        const dayName = daysOfWeek[new Date(day.date).getDay()];
                        const stats = day.stats;

                        return (
                            <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f5f5f5' }}>
                                <td style={{ border: '1px solid #ddd', padding: '10px' }}>
                                    {day.date} ({dayName})
                                </td>
                                <td style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'center' }}>
                                    {dailyPercent}% ({completed}/{total})
                                </td>
                                <td style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'center' }}>
                                    {stats.weight > 0 ? stats.weight.toFixed(1) : '-'}
                                </td>
                                <td style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'center' }}>
                                    {stats.sleepTime > 0 ? stats.sleepTime.toFixed(1) : '-'}
                                </td>
                                <td style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'center' }}>
                                    {stats.waterIntake > 0 ? stats.waterIntake.toFixed(1) : '-'}
                                </td>
                                <td style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'center' }}>
                                    {stats.exerciseTime > 0 ? stats.exerciseTime : '-'}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {/* Footer: Mango | Nutrition Trends */}
            <div style={{ marginTop: '50px', paddingTop: '15px', borderTop: '1px solid #ddd', textAlign: 'center', fontSize: '0.8em', color: '#666' }}>
                <p style={{ margin: '5px 0' }}>Generated by Mango | Nutrition Trends Tracker.</p>
                <p style={{ margin: '5px 0' }}>Confidential Client Report - {new Date().getFullYear()}</p>
            </div>
        </div>
    );
};

// --- Main Exported Component ---
const WeeklyReportSummary = ({ uid, dietPlanData }) => {
    const [userGoalsAndCurrentStats, setUserGoalsAndCurrentStats] = useState({});
    const [weeklyProgress, setWeeklyProgress] = useState([]);
    const [loading, setLoading] = useState(true);
    const reportRef = useRef(); 

    useEffect(() => {
        const fetchAllData = async () => {
            if (!uid) return;
            setLoading(true);

            try {
                // --- 1. Fetch Daily Progress Data (meal adherence, daily logs) ---
                const dates = getLastSevenDays();
                const progressCollectionRef = collection(db, 'progress', uid, 'dailyProgress');
                const progressPromises = dates.map(date => getDoc(doc(progressCollectionRef, date)));
                const progressDocSnaps = await Promise.all(progressPromises);

                const progressData = progressDocSnaps.map((snap, index) => {
                    const data = snap.data();
                    
                    // This logic correctly reads the direct fields
                    return {
                        date: dates[index],
                        stats: {
                            weight: data?.currentWeight || 0, 
                            sleepTime: data?.sleepTime || 0,
                            waterIntake: data?.waterIntake || 0,
                            exerciseTime: data?.exerciseTime || 0,
                        },
                        adherence: data?.mealAdherence || [],
                    };
                });
                
                setWeeklyProgress(progressData);
                
                // --- 2. Fetch Overall User Stats and Goals ---
                const userStatsDocRef = doc(db, 'userStats', uid); 
                const userStatsDocSnap = await getDoc(userStatsDocRef);
                
                if (userStatsDocSnap.exists()) {
                    const stats = userStatsDocSnap.data();
                    setUserGoalsAndCurrentStats({
                        currentWeight: stats.currentWeight || 0,
                        initialWeight: stats.initialWeight || 0,
                        sleepGoal: stats.sleepGoal || 0,
                        waterGoal: stats.waterGoal || 0,
                        weightGoal: stats.weightGoal || 0, 
                    });
                }

            } catch (error) {
                console.error("Error fetching data for weekly progress:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchAllData();
    }, [uid]);


    const handleDownloadReport = async () => {
        if (!reportRef.current) return;

        const element = reportRef.current; 

        // 1. Capture the DOM element as a canvas (image)
        const canvas = await html2canvas(element, { 
            scale: 2, 
            logging: false,
            useCORS: true, 
            allowTaint: true, 
            backgroundColor: '#f9f9f9',
        });
        const imgData = canvas.toDataURL('image/jpeg', 1.0);
        
        // 2. Initialize jsPDF (multi-page handling logic)
        const pdf = new jsPDF('p', 'mm', 'a4'); 
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        
        const imgProps = pdf.getImageProperties(imgData);
        const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;
        
        let heightLeft = imgHeight;
        let position = 0;
        
        pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, imgHeight);
        heightLeft -= pdfHeight;

        while (heightLeft >= -1) { 
            position = heightLeft - imgHeight;
            pdf.addPage();
            pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, imgHeight);
            heightLeft -= pdfHeight;
        }

        // 3. Download the PDF
        pdf.save(`Weekly_Nutrition_Report_${new Date().toISOString().split('T')[0]}.pdf`);
    };
    
    // Summary Calculations for Dashboard display (uses all days)
    const totalMeals = dietPlanData?.mealTimings?.length * 7 || 0;
    const completedMeals = weeklyProgress.reduce((sum, day) => {
        return sum + (Array.isArray(day.adherence) ? day.adherence.filter(a => a).length : 0);
    }, 0);
    const adherencePercentage = totalMeals > 0 ? ((completedMeals / totalMeals) * 100).toFixed(0) : 'N/A';
    
    const weightLoss = userGoalsAndCurrentStats.initialWeight && userGoalsAndCurrentStats.currentWeight 
        ? (userGoalsAndCurrentStats.initialWeight - userGoalsAndCurrentStats.currentWeight) 
        : 0;
    
    const loggedDaysCount = weeklyProgress.filter(day => 
        (Array.isArray(day.adherence) && day.adherence.length > 0) || 
        day.stats.weight > 0 || 
        day.stats.sleepTime > 0 || 
        day.stats.waterIntake > 0 ||
        day.stats.exerciseTime > 0
    ).length;


    // The actual component render
    return (
        <div className="card summary-card full-width">
            <h3>📝 Weekly Progress Report</h3>
            
            {/* ⚠️ The hidden element that jspdf/html2canvas will capture */}
            <div ref={reportRef} style={{ position: 'absolute', left: '-9999px', zIndex: -1 }}>
                <ReportContent 
                    weeklyProgress={weeklyProgress} 
                    dietPlanData={dietPlanData}
                    userGoalsAndCurrentStats={userGoalsAndCurrentStats}
                />
            </div>

            {loading ? (
                <p>Loading weekly data...</p>
            ) : (
                <>
                    <p>You logged adherence for {loggedDaysCount} out of 7 days.</p>
                    <div className="summary-details">
                        <p>Meals Followed: {adherencePercentage}%</p>
                        <p>Total Weight Change: {weightLoss.toFixed(1)} kgs</p>
                    </div>
                    
                    <button className="report-button" onClick={handleDownloadReport}>
                        ⬇️ Download Weekly Report (PDF)
                    </button>
                </>
            )}
        </div>
    );
};

export default WeeklyReportSummary;